import { getModules } from '@/core/hooks/useStore';
import { PaginatedResponse } from '@/core/models/PaginatedResponse';
import { StoreState } from '@/core/models/store.interface';
import { LocationQuery } from 'vue-router';
import { Module } from 'vuex';
import { CertificateState } from './certificates/certificate.store';
import { DocumentUrlState } from './document-urls/document-url.store';
import { EmployeeDocumentState } from './documents/document.store';
import { Employee } from './employee.interface';
import { EmployeeService } from './employee.service';
import { PerformanceCounterState } from './performance-counters/performance-counter.store';
import { PropertyTypeCompanyState } from './property-type-companies/property-type-company.store';

const requiredModules = import.meta.glob('./*/*.store.ts', { eager: true });
const modules = getModules(requiredModules);

const service = new EmployeeService();

export interface EmployeeState {
   employeesOverview?: PaginatedResponse<Employee>;

   // submodules
   certificate?: CertificateState;
   document?: EmployeeDocumentState;
   documentUrl?: DocumentUrlState;
   performanceCounter?: PerformanceCounterState;
   propertyTypeCompany?: PropertyTypeCompanyState;
}

const EmployeeStore: Module<EmployeeState, StoreState> = {
   namespaced: true,
   state: {},
   getters: {},
   mutations: {
      SET_EMPLOYEES_OVERVIEW(state: EmployeeState, employeesOverview: PaginatedResponse<Employee>) {
         state.employeesOverview = employeesOverview;
      },
      ADD_EMPLOYEE(state: EmployeeState, employee: Employee) {
         state.employeesOverview?.data?.push(employee);
      },
   },
   actions: {
      async getEmployees(context: any, query: LocationQuery) {
         const response = await service.getEmployees(query);

         context.commit('SET_EMPLOYEES_OVERVIEW', response);
         return response;
      },

      async getEmployee(context: any, { employeeId, query }: { employeeId: number; query: LocationQuery }) {
         return await service.getEmployee(employeeId, query);
      },

      async createEmployee(context: any, employee: Employee) {
         const response = await service.postEmployee(employee);
         context.commit('ADD_EMPLOYEE', response);
         return response;
      },
      async changeEmployee(context: any, data: { employeeId: number; employee: Employee }) {
         return await service.putEmployee(data.employeeId, data.employee);
      },
      async deleteEmployee(context: any, employee_id: number) {
         return service.deleteEmployee(employee_id);
      },
      async getEmployeeById(context: any, employeeId: number) {
         return await service.getEmployeeById(employeeId);
      },
      async searchEmployees(context: any, query: LocationQuery) {
         return await service.searchEmployees(query);
      },
      async getEmployeesByCompany(context: any, companyId: number) {
         return service.getEmployeesByCompany(companyId);
      },
      async importEmployeesTranslations(context, file: File) {
         return await service.importEmployeesTranslations(file);
      },
      async importEmployees(context, payload: { file: File; employeeMappings: { [key: string]: any } }) {
         const batch = await service.importEmployees(payload.file, payload.employeeMappings);
         context.commit('batch/ADD_BATCH', batch, { root: true });
         return batch;
      },
   },
   modules,
};

export default EmployeeStore;
